<template>
  <v-dialog :value="dialog" @input="$emit('input', $event)" persistent>
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-if="!readonly"
        v-bind="attrs"
        v-on="on"
        class="mx-2"
        color="primary"
      >
        「新規登録」はこのボタンを押してください
      </v-btn>
    </template>
    <v-form ref="form" v-model="valid">
      <v-card>
        <v-card-title>
          <span class="headlline">{{ dialogTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col :cols="7">
                <!-- テンプレート名称 -->
                <v-text-field
                  :value="template_name"
                  @input="$emit('update:template_name', $event)"
                  label="テンプレート名称"
                  :placeholder="config.PLACEHOLDER_INPUT"
                  :outlined="!readonly"
                  :readonly="readonly"
                  :maxlength="30"
                  :rules="[validationRules.required()]"
                  counter
                ></v-text-field>
              </v-col>
              <v-spacer></v-spacer>
            </v-row>
            <v-row>
              <v-col cols="3">
                <!-- 配信先キャリア -->
                <v-text-field
                  v-if="readonlyDeliveryCa"
                  :value="deliveryCaName"
                  label="配信先キャリア"
                  readonly
                  hide-details
                ></v-text-field>
                <v-select
                  v-else
                  :value="delivery_ca"
                  @input="$emit('update:delivery_ca', $event)"
                  :items="deliveryCas"
                  :placeholder="config.PLACEHOLDER_SELECT"
                  :rules="[validationRules.required()]"
                  label="配信先キャリア"
                  item-text="name"
                  item-value="code"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="3">
                <!-- 緊急事態区分 -->
                <v-text-field
                  v-if="readonlyEmergCls"
                  :value="emergClsName"
                  label="緊急事態区分"
                  readonly
                  hide-details
                ></v-text-field>
                <v-select
                  v-else
                  :value="emerg_cls"
                  @input="$emit('update:emerg_cls', $event)"
                  :items="emergClss"
                  :placeholder="config.PLACEHOLDER_SELECT"
                  :rules="[validationRules.required()]"
                  label="緊急事態区分"
                  item-text="name"
                  item-value="code"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="3">
                <!-- 用途 -->
                <v-text-field
                  v-if="readonlyStaffOnlyFlg"
                  :value="staffOnlyFlgName"
                  label="用途"
                  readonly
                  hide-details
                ></v-text-field>
                <v-select
                  v-else
                  :value="staff_only_flg"
                  @input="$emit('update:staff_only_flg', $event)"
                  :items="staffOnlyFlgs"
                  :placeholder="config.PLACEHOLDER_SELECT"
                  :rules="[validationRules.required()]"
                  label="用途"
                  item-text="name"
                  item-value="code"
                  outlined
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <!-- 言語タブ -->
              <v-tabs v-model="selectedTabIndex" color="black">
                <v-tab
                  v-for="{ code, name } in locales"
                  :key="code"
                  :disabled="!enableTabPages[code]"
                >
                  {{ name }}
                </v-tab>
                <v-tabs-items v-model="selectedTabIndex">
                  <v-tab-item v-for="{ code } in locales" :key="code" eager>
                    <edit-tab-page
                      ref="tabPages"
                      v-if="enableTabPages[code]"
                      :readonly="readonly"
                      :isAlwaysRequired="code === primaryLocaleCode"
                      :validationRules="validationRules"
                      :enableAttributes="enableAttributes"
                      :deliveryCas="deliveryCas"
                      :emergClss="emergClss"
                      :staffOnlyFlgs="staffOnlyFlgs"
                      :oils="oils"
                      :urlPreviewed.sync="
                        urlPreviewedOf[delivery_ca || deliveryCas[0].code][code]
                      "
                      v-bind.sync="self[code]"
                    >
                    </edit-tab-page>
                  </v-tab-item>
                </v-tabs-items>
              </v-tabs>
            </v-row>
          </v-container>
          <v-card-actions v-if="readonly">
            <v-row>
              <v-spacer></v-spacer>
              <v-btn class="mx-2" color="primary" @click="handleClose">
                戻る
              </v-btn>
            </v-row>
          </v-card-actions>
          <v-card-actions v-else>
            <v-row>
              <v-spacer></v-spacer>
              <v-btn
                class="mx-2"
                color="primary"
                :loading="loading"
                :disabled="!(valid && allPreviewed)"
                @click="handleSave"
              >
                {{ buttonName }}
              </v-btn>
              <v-btn
                class="mx-2"
                color="primary"
                :disabled="loading"
                @click="handleClose"
              >
                キャンセル
              </v-btn>
            </v-row>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-form>
    <!-- ダイアログ -->
    <error-dialog ref="errorDialog" />
  </v-dialog>
</template>

<script>
import config from "@/js/const";
import ErrorDialog from "@/components/ErrorDialog";
import messageUtils from "@/mixins/message-utils";
import EditTabPage from "@/components/disaster-fixedphrase-mst/EditTabPage";

export default {
  model: {
    prop: "dialog",
    event: "input"
  },

  mixins: [messageUtils],

  components: {
    ErrorDialog,
    EditTabPage
  },

  props: {
    // このダイアログを表示するかどうかを示す値
    dialog: Boolean,
    // ローディング状態を示す値
    loading: Boolean,
    // 読み取り専用かどうかを示す値
    readonly: Boolean,
    // バリデーションルール
    validationRules: Object,
    // 配信先キャリアコードリスト
    deliveryCas: Array,
    // 緊急事態区分リスト
    emergClss: Array,
    // 用途リスト
    staffOnlyFlgs: Array,
    // 言語リスト
    locales: Array,
    // 主言語コード
    primaryLocaleCode: String,
    // OILリスト
    oils: Array,
    // 配信先キャリア別の有効な属性名リスト
    enableAttributesMap: Object,
    // 多言語が入力可能かどうかを判定する関数
    multiLocaleCriteria: Function,
    // 新規登録かどうかを示す値
    isNew: Boolean,
    // 配信先キャリアコード
    delivery_ca: String,
    // 緊急事態区分コード
    emerg_cls: String,
    // テンプレート名称
    template_name: String,
    // 用途コード
    staff_only_flg: String,
    // 言語: 日本語
    ja: Object,
    // 言語: 英語
    en: Object,
    // 言語: 韓国語
    ko: Object,
    // 言語: 中国語（簡体）
    zhcn: Object,
    // 言語: 中国語（繁体）
    zhtw: Object
  },

  data: instance => ({
    self: instance,
    config,
    // フォームの検証が成功しているかどうかを示す値
    valid: false,
    // 選択中のタブ
    selectedTabIndex: 0,
    // プレビュー設定
    urlPreviewedOf: instance.createUrlPreviewed()
  }),

  watch: {
    dialog(value) {
      // ダイアログが表示時に初期化する。
      if (value) {
        this.initialize();
      }
    },

    /**
     * 配信先キャリアの変更を監視
     */
    delivery_ca() {
      this.selectedTabIndex = 0;
    },

    /**
     * 補足情報の変更を監視
     */
    staff_only_flg() {
      this.selectedTabIndex = 0;
    }
  },

  computed: {
    /**
     * ダイアログタイトル
     */
    dialogTitle() {
      return `災害配信定型文マスタの${
        this.readonly ? "詳細" : this.isNew ? "登録" : "編集"
      }`;
    },

    /**
     * ボタン名
     */
    buttonName() {
      return this.isNew ? "登録" : "更新";
    },

    /**
     * 配信先キャリアを読み取り専用にするかどうかを示す値
     */
    readonlyDeliveryCa() {
      return this.readonly || !this.isNew;
    },

    /**
     * 配信先キャリア名
     */
    deliveryCaName() {
      return (
        this.deliveryCas.find(elem => elem.code === this.delivery_ca)?.name ??
        ""
      );
    },

    /**
     * 緊急事態区分を読み取り専用にするかどうかを示す値
     */
    readonlyEmergCls() {
      return this.readonly;
    },

    /**
     * 緊急事態区分名
     */
    emergClsName() {
      return (
        this.emergClss.find(elem => elem.code === this.emerg_cls)?.name ?? ""
      );
    },

    /**
     * 用途を読み取り専用にするかどうかを示す値
     */
    readonlyStaffOnlyFlg() {
      return this.readonly || !this.isNew;
    },

    /**
      用途名
     */
    staffOnlyFlgName() {
      return (
        this.staffOnlyFlgs.find(elem => elem.code === this.staff_only_flg)
          ?.name ?? ""
      );
    },

    /**
     * 現在表示中の配信先キャリアの有効な属性
     */
    enableAttributes() {
      return this.enableAttributesMap[this.delivery_ca] ?? [];
    },

    /**
     * 多言語設定が必要かどうかを示す値
     */
    needsMultiLocale() {
      return this.multiLocaleCriteria({
        delivery_ca: this.delivery_ca,
        staff_only_flg: this.staff_only_flg
      });
    },

    /**
     * 主言語の必須項目が設定されているかどうかを示す値
     */
    inputsPrimaryLocaleFields() {
      const { fixed_phrase_title, fixed_phrase } = this[this.primaryLocaleCode];
      return !!fixed_phrase_title && !!fixed_phrase;
    },

    /**
     * 言語毎のタブページを有効にするかを示す値を保持するオブジェクト
     */
    enableTabPages() {
      return Object.fromEntries(
        this.locales.map(({ code }) => [
          code,
          // 言語オブジェクトが存在する（参照時はAPIのレスポンスに従う）
          !!this[code] &&
            // 主言語
            (code === this.primaryLocaleCode ||
              // 多言語設定が必要、かつ、主言語必須入力済み
              (this.needsMultiLocale && this.inputsPrimaryLocaleFields))
        ])
      );
    },

    /**
     * プレビュー済みかどうかを示す値
     */
    allPreviewed() {
      return Object.values(
        this.urlPreviewedOf[this.delivery_ca || this.deliveryCas[0].code]
      ).every(value => value);
    }
  },

  methods: {
    /**
     * コンポーネントを初期化する。
     */
    initialize() {
      this.$nextTick(() => {
        // スクロールのリセット
        const elements = document.getElementsByClassName("v-dialog--active");
        if (elements && elements.length > 0) {
          elements[0].scrollTop = 0;
        }
        // バリデーションのリセット
        this.$refs.form.resetValidation();
      });

      this.urlPreviewedOf = this.createUrlPreviewed();
    },

    /**
     * urlPreviewedの初期値を作成する。
     * @returns {object} 配信先キャリアコード、言語コード別のフラグ値。
     */
    createUrlPreviewed() {
      return Object.fromEntries(
        this.deliveryCas.map(({ code: deliveryCa }) => [
          deliveryCa,
          Object.fromEntries(
            this.locales.map(({ code: locale }) => [locale, true])
          )
        ])
      );
    },

    /**
     * 登録または更新ボタンのクリックイベントを処理する。
     */
    handleSave() {
      const form = this.$refs.form;

      // 入力フォームのバリデーションチェック
      if (!form.validate()) {
        // バリデーションエラー項目がタブ内コントロールかどうかを取得する。
        const invalidInput = form.inputs.find(input => !input.valid);
        const invalidTabIndex = this.$refs.tabPages.findIndex(page =>
          Object.values(page.$refs).some(input => input === invalidInput)
        );

        if (invalidTabIndex >= 0) {
          this.$nextTick(() => (this.selectedTabIndex = invalidTabIndex));
        }

        return;
      }

      this.$emit("save");
    },

    /**
     * キャンセルまたは戻るボタンのクリックイベントを処理する。
     */
    handleClose() {
      this.$emit("close");
    },

    /**
     * エラーダイアログを表示する。
     * @param {...string} messages メッセージ
     */
    async showErrorDialog(...messages) {
      await this.$refs.errorDialog.open(messages);
    }
  }
};
</script>

<style scoped>
.v-tab-item {
  padding: 10px;
}

.v-tab {
  background-color: #dcdcdc;
  color: black;
}
</style>
